import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    TimePicker,
    DatePicker,
} from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';


const { TextArea } = Input;
const { Option } = Select;

dayjs.extend(duration);


interface AttendanceModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        attendance_id?: number;
        attendance_ledger_id?: number;
        attendance_date?: string;
        attendance_in: string;
        attendance_out: string;
        attendance_type: string;
        attendance_remark: string;
    } | null;
    isEditMode?: boolean;
}

const attendanceTypeOptions = [
    { label: 'Unpaid Leave', value: 'Unpaid Leave' },
    { label: 'Half Day', value: 'Half Day' },
    { label: 'Present', value: 'Present' },
    { label: 'Holiday', value: 'Holiday' },
    { label: 'Paid Leave', value: 'Paid Leave' },
    { label: 'auto', value: 'auto' },
];


const EmployeeAttendanceModal: React.FC<AttendanceModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddAttendance = async (values: any) => {
        try {
            const dataVal = {
                attendance_date: values.attendance_date
                    ? values.attendance_date.format("YYYY-MM-DD")
                    : null,
                attendance_in: values.attendance_in ? values.attendance_in.format('hh:mm:ss A')  // e.g. "01:30:00 PM"
                    : null,
                attendance_out: values.attendance_out ? values.attendance_out.format('hh:mm:ss A')  // e.g. "01:30:00 PM"
                    : null,
                attendance_type: values.attendance_type,
                attendance_remark: values.attendance_remark,
                ...(initialValues?.attendance_id && { attendance_id: initialValues.attendance_id }),
            };
            const url = isEditMode
                ? 'edit-attendance'
                : 'add-attendance';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'attendance updated successfully!' : ' attendance created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} attendance`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                attendance_date: initialValues.attendance_date
                    ? dayjs(initialValues.attendance_date) // Converts ISO → Dayjs
                    : null,
                // FIX: parse 24-hour backend time correctly
                attendance_in: initialValues.attendance_in
                    ? dayjs(initialValues.attendance_in, "HH:mm:ss")
                    : null,

                attendance_out: initialValues.attendance_out
                    ? dayjs(initialValues.attendance_out, "HH:mm:ss")
                    : null,
                attendance_type: initialValues.attendance_type,
                attendance_remark: initialValues.attendance_remark,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);




    return (
        <Modal
            title={isEditMode ? 'Edit attendance' : 'Add New attendance'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddAttendance}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="attendance_date"
                            label="Attendance Date"
                            rules={[{ required: true, message: "Please select date" }]}
                        >
                            <DatePicker
                                format="DD-MM-YYYY"
                                style={{ width: "100%", padding: 10 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="attendance_in"
                            className="px-8 "
                            label="Check-In Time"
                            rules={[{ required: true, message: 'Please enter Attendance In' }]}
                        >
                            <TimePicker format="hh:mm A" style={{ width: "100%" }}
                                className="simple-input-radius-with-border" use12Hours />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="attendance_out"
                            className="px-8 "
                            label="Check-In Time"
                            rules={[{ required: true, message: 'Please enter Attendance Out' }]}
                        >
                            <TimePicker format="hh:mm A" style={{ width: "100%" }}
                                className="simple-input-radius-with-border" use12Hours />
                        </Form.Item>
                    </Col>
                    {/* Type */}
                    <Col span={12}>
                        <Form.Item
                            name="attendance_type"
                            className="px-9"
                            label="Type of Work"
                            rules={[
                                { required: true, message: 'Please enter Type of Attendance' },
                                { max: 50, message: 'Maximum 50 characters allowed' },
                            ]}
                        >
                            <Select
                                showSearch
                                placeholder="Select type of work"
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={attendanceTypeOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Remark"
                            name="attendance_remark"
                            rules={[{ required: true, message: "Please enter a remark" }]}
                        >
                            <TextArea
                                rows={4}
                                placeholder="Enter your remark"
                            />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Attendance
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EmployeeAttendanceModal;
